import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Check } from 'lucide-react';
import { MarketingShell } from './MarketingShell';
import { usePricingPagePackages } from '../../api/catalog';
import { toMajor } from '../../contracts/money';
import { useBrand } from '../../brand/BrandProvider';
import { useCms, cmsText, cmsList, type CmsListSectionValue } from '../../api/cms';
import { isItcityHost } from '../../brand/host';
import { SINGLE_FALLBACKS, LIST_FALLBACKS } from '../../content/fallbacks';

/**
 * Public pricing — host-aware (Decision #21). Live from GET /pricing, which the
 * API scopes by hostname: a reseller's domain shows that reseller's OWN published
 * packages under the reseller's brand; the platform/direct host shows Possignin's
 * own plans. The page copy follows the resolved brand so nothing ITCity/Possignin
 * leaks on a reseller's customer-facing page. The headline/intro (pricing.intro) is
 * CMS-editable too — host-aware useCms() means a reseller's own override shows on
 * their host.
 */

export function Pricing() {
  const { data: plans = [] } = usePricingPagePackages();
  const brand = useBrand();
  const isReseller = !brand.isPlatform;
  const { data: cms } = useCms();

  // Interval coverage drives the UI: show the monthly/yearly switch ONLY when both
  // intervals are present for EVERY card — otherwise one view would render ৳0.
  // Default to whichever interval is fully populated (monthly first; reseller
  // catalogs are often yearly-only). The user's explicit pick wins once they toggle.
  const allMonthly = plans.length > 0 && plans.every((p) => p.priceMonthlyMinor != null);
  const allYearly = plans.length > 0 && plans.every((p) => p.priceYearlyMinor != null);
  const anyMonthly = plans.some((p) => p.priceMonthlyMinor != null);
  const showToggle = allMonthly && allYearly;
  const defaultBilling: 'monthly' | 'yearly' = allMonthly ? 'monthly' : allYearly ? 'yearly' : anyMonthly ? 'monthly' : 'yearly';
  const [billingOverride, setBillingOverride] = useState<'monthly' | 'yearly' | null>(null);
  const billing: 'monthly' | 'yearly' = showToggle ? billingOverride ?? 'monthly' : defaultBilling;

  // /pricing resolves on BOTH platform hosts. ITCity reads its OWN keys first and falls
  // back to the Possignin copy per field, so editing one site no longer rewrites the other
  // and itcity.ltd is unchanged until it is actually given its own words. Same split as
  // `company.blog*` vs `blog.*`.
  const itcity = isItcityHost();
  const pText = (field: string, dflt: string) =>
    itcity
      ? cmsText(cms, 'company.pricingIntro', field, cmsText(cms, 'pricing.intro', field, dflt))
      : cmsText(cms, 'pricing.intro', field, dflt);

  const headline = pText('headline', SINGLE_FALLBACKS['pricing.intro']!.headline!);
  const defaultSubhead = `Start free for 7 days, no card. Pick a plan when you’re ready. ${
    isReseller ? `Pay online with SSLCommerz, or pay ${brand.name} directly.` : 'Pay online with SSLCommerz or cash to our Sylhet office.'
  }`;
  const subhead = pText('subhead', defaultSubhead);
  const assurancesFallback = LIST_FALLBACKS['pricing.assurances']!;
  // Same emptiness basis cmsList() uses (row exists AND has items) — NOT raw row
  // truthiness. The API now deletes a LIST row outright when it's saved with 0
  // items (see cms.service.ts `set()`), but this stays as defense in depth: a
  // row-truthiness check here would keep rendering the platform's ITCity-specific
  // fallback body on a reseller's own branded page for an empty-but-present row.
  // ITCity's own list wins when it has items; an empty one falls through to Possignin's.
  const itcityAssurances = itcity ? cmsList(cms, 'company.pricingAssurances', []) : [];
  const assurancesKey = itcityAssurances.length > 0 ? 'company.pricingAssurances' : 'pricing.assurances';
  const cmsAssurancesSection = cms?.[assurancesKey] as CmsListSectionValue | undefined;
  const hasCmsAssurances = Array.isArray(cmsAssurancesSection?.items) && cmsAssurancesSection.items.length > 0;
  const assurances = cmsList(cms, assurancesKey, assurancesFallback).map((a, i) =>
    // The 3rd card ("Pay your way") stays brand-computed when not overridden by
    // the CMS, exactly like the subhead above — same reasoning, same pattern.
    i === 2 && !hasCmsAssurances
      ? {
          title: a.title,
          body: isReseller
            ? `SSLCommerz for online, or pay ${brand.name} directly in cash.`
            : 'SSLCommerz for online, or cash to our Sylhet office. Resold by a partner? Pay them in cash.',
        }
      : a,
  );
  const contactLine = itcity
    ? cmsText(cms, 'company.pricingContactLine', 'text', cmsText(cms, 'pricing.contactLine', 'text', SINGLE_FALLBACKS['pricing.contactLine']!.text!))
    : cmsText(cms, 'pricing.contactLine', 'text', SINGLE_FALLBACKS['pricing.contactLine']!.text!);

  return (
    <MarketingShell>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-10 text-center">
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
          {headline}
        </h1>
        <p className="mt-4 text-lg text-zinc-600 dark:text-zinc-300 max-w-xl mx-auto leading-relaxed">
          {subhead}
        </p>

        {showToggle && (
          <div className="mt-8 inline-flex items-center gap-1 p-1 rounded-full border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
            <button
              type="button"
              onClick={() => setBillingOverride('monthly')}
              className={'px-4 py-1.5 rounded-full text-sm font-medium transition ' + (billing === 'monthly' ? 'bg-white dark:bg-zinc-800 text-zinc-950 dark:text-zinc-50 shadow-sm' : 'text-zinc-500 dark:text-zinc-400')}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBillingOverride('yearly')}
              className={'inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition ' + (billing === 'yearly' ? 'bg-white dark:bg-zinc-800 text-zinc-950 dark:text-zinc-50 shadow-sm' : 'text-zinc-500 dark:text-zinc-400')}
            >
              Yearly
              <span className="px-1.5 py-px rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                2 months free
              </span>
            </button>
          </div>
        )}
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-12 grid md:grid-cols-3 gap-5 items-start">
        {plans.map((p, idx) => {
          // Prefer the selected interval; fall back to the card's other interval so a
          // package priced on only one interval never renders ৳0. The unit label
          // follows whichever price we actually show.
          const selMinor = billing === 'monthly' ? p.priceMonthlyMinor : p.priceYearlyMinor;
          const otherMinor = billing === 'monthly' ? p.priceYearlyMinor : p.priceMonthlyMinor;
          const usedMinor = selMinor ?? otherMinor;
          const usedInterval: 'monthly' | 'yearly' = selMinor != null ? billing : billing === 'monthly' ? 'yearly' : 'monthly';
          const amount = usedMinor != null ? toMajor(usedMinor) : 0;
          const popular = plans.length === 3 && idx === 1; // highlight the middle card only on a 3-plan layout
          // Invoice cap differs by interval — resolve to the interval actually shown.
          // API returns invoicesMonthly/invoicesYearly (null/absent = unlimited); insert
          // the line after the location/user/product caps, before the module rows.
          const invCap = usedInterval === 'monthly' ? p.invoicesMonthly : p.invoicesYearly;
          const invLine =
            invCap == null
              ? 'Unlimited invoices'
              : `${invCap.toLocaleString('en-US')} invoices / ${usedInterval === 'monthly' ? 'mo' : 'yr'}`;
          const feats = [...p.features.slice(0, 3), invLine, ...p.features.slice(3)];
          return (
            <div
              key={p.id}
              className={
                'relative rounded-2xl border bg-white dark:bg-zinc-900/50 p-6 sm:p-7 flex flex-col ' +
                (popular ? 'border-brand shadow-md ring-1 ring-brand/20' : 'border-zinc-200 dark:border-zinc-800')
              }
            >
              {popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-brand text-white">
                  Most popular
                </span>
              )}
              <h2 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">{p.name}</h2>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{p.tagline}</p>

              <div className="mt-5 flex items-baseline gap-1">
                <span className="text-4xl font-semibold tabular-nums text-zinc-950 dark:text-zinc-50">৳{amount.toLocaleString('en-US')}</span>
                <span className="text-sm text-zinc-500 dark:text-zinc-400">/{usedInterval === 'monthly' ? 'month' : 'year'}</span>
              </div>

              <Link
                to="/signup"
                className={
                  'mt-6 inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 rounded-lg text-sm font-medium transition ' +
                  (popular
                    ? 'bg-brand text-white hover:bg-brand/90'
                    : 'border border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-zinc-50 hover:bg-zinc-50 dark:hover:bg-zinc-800')
                }
              >
                Start free trial <ArrowRight className="w-3.5 h-3.5" />
              </Link>

              <ul className="mt-6 space-y-2.5 text-sm text-zinc-700 dark:text-zinc-200">
                {feats.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-8 grid sm:grid-cols-3 gap-4">
        {assurances.map((f, i) => (
          <div key={i} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50/60 dark:bg-zinc-900/40 p-5">
            <h3 className="font-semibold text-zinc-950 dark:text-zinc-50">{f.title}</h3>
            <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{f.body}</p>
          </div>
        ))}
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20 text-center">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          {contactLine}{' '}
          <a href={`mailto:${brand.supportEmail}`} className="font-medium text-brand dark:text-brand-dm hover:underline">Talk to us</a>
          {/* "become a reseller" is a platform-only CTA — never surface it on a reseller's own page. */}
          {!isReseller && (
            <>
              {' '}— or{' '}
              <Link to="/become-a-reseller" className="font-medium text-brand dark:text-brand-dm hover:underline">become a reseller</Link>
            </>
          )}
          .
        </p>
      </section>
    </MarketingShell>
  );
}
